import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /** @type {HTMLDetailsElement | null} */
  #detailsEl = null;

  connectedCallback() {
    super.connectedCallback();
    // KIYO: collapse the open discount input on an outside click (e.g. tapping the
    // product list above or the totals below). The "+" toggle is hidden once the
    // field is open, so without this there'd be no way to close it again.
    document.addEventListener('click', this.#handleOutsideClick);
    // KIYO: clear any error when the field collapses, so reopening starts clean.
    const details = this.closest('details');
    if (details instanceof HTMLDetailsElement) {
      this.#detailsEl = details;
      details.addEventListener('toggle', this.#handleDetailsToggle);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleOutsideClick);
    this.#detailsEl?.removeEventListener('toggle', this.#handleDetailsToggle);
    this.#detailsEl = null;
  }

  /**
   * Clears discount errors when the field collapses → a fresh reopen shows no
   * stale error.
   * @param {Event} event
   */
  #handleDetailsToggle = (event) => {
    const details = event.currentTarget;
    if (details instanceof HTMLDetailsElement && !details.open) this.#resetErrors();
  };

  /** Hides all discount error messages. */
  #resetErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError?.classList.add('hidden');
    cartDiscountErrorDiscountCode?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');
    this.refs.cartDiscountErrorGeneric?.classList.add('hidden');
  }

  /**
   * Collapses the open discount <details> on an outside click — unless an applied
   * discount pill is showing (an active code must stay visible).
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    const details = this.closest('details');
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (this.querySelector('.cart-discount__pill')) return; // active code → keep open
    const wrapper = this.closest('.cart-discount') ?? details;
    if (event.target instanceof Node && wrapper.contains(event.target)) return; // click inside → ignore
    details.open = false;
  };

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      // KIYO: also reset the generic error line so it can't linger next to another message.
      this.refs.cartDiscountErrorGeneric?.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        // KIYO: deliberately NOT detaching the rejected code — Shopify keeps it
        // on the cart and auto-applies it once its conditions are met (e.g. the
        // minimum cart value is reached). That's wanted: the customer shouldn't
        // lose the discount. The pill then becomes visible via declarative-open
        // in cart-summary.liquid. Details: THEME-CHANGES.md 2026-07-23.
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      // KIYO: don't swallow failures silently. A prior in-flight request aborted
      // by #createAbortController (rapid re-submit) is expected — ignore it.
      // Anything else (network/offline, non-JSON 4xx/5xx) gets a visible generic
      // error instead of a silent no-op right before checkout.
      if (error?.name !== 'AbortError') {
        console.error(error);
        this.#handleDiscountError('generic');
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      // KIYO: `Element` instead of `HTMLElement` — a click hitting the × button's
      // svg/path icon (an SVGElement, half the button's hit area) was silently
      // swallowed, so removing a discount often needed several clicks.
      // `closest()` below works on SVG elements too. THEME-CHANGES.md 2026-07-23.
      !(event.target instanceof Element) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      // KIYO: surface the failure in the console. On failure no morph ran, so the
      // pill is left untouched — the unchanged UI already signals "nothing
      // happened" to the user (no false-removed state to recover from).
      if (error?.name !== 'AbortError') console.error(error);
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'generic'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    let target;
    if (type === 'discount_code') {
      target = cartDiscountErrorDiscountCode;
    } else if (type === 'shipping') {
      target = cartDiscountErrorShipping;
    } else {
      // KIYO: generic network/server error line. Accessed defensively (not in
      // requiredRefs) so a theme update that drops the ref can't break the component.
      target = this.refs.cartDiscountErrorGeneric;
    }
    cartDiscountError.classList.remove('hidden');
    target?.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
